import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { FC, useMemo } from 'react';

import {
  getVertriebsStatus,
  Interessent,
  interessentenStatusOptions,
  InteressentenStatus,
} from '../../kunde/api/types';

interface InteressentFunnelProps {
  interessenten: Interessent[];
  selectedStatus: InteressentenStatus | '';
  onStatusChange: (status: InteressentenStatus | '') => void;
}

/** Die vier Stufen, die ein Interessent nacheinander durchläuft. */
const funnelStages = [
  InteressentenStatus.NEU,
  InteressentenStatus.ERSTGESPRAECH_ERFOLGT,
  InteressentenStatus.ANGEBOT,
  InteressentenStatus.NACHGEFASST,
];

/** Abschließende Ausgänge, die keine weiteren Stufen im Vertriebsprozess sind. */
const funnelOutcomes = [
  InteressentenStatus.ANGENOMMEN,
  InteressentenStatus.NICHT_ANGENOMMEN,
  InteressentenStatus.INAKTIV,
];

const getLabel = (status: InteressentenStatus) =>
  interessentenStatusOptions.find(option => option.value === status)!.label;

const getAccentColor = (theme: Theme, status: InteressentenStatus) => {
  if (status === InteressentenStatus.ANGENOMMEN) {
    return theme.palette.success.main;
  }

  if (status === InteressentenStatus.NICHT_ANGENOMMEN) {
    return theme.palette.error.main;
  }

  if (status === InteressentenStatus.INAKTIV) {
    return theme.palette.text.secondary;
  }

  return theme.palette.secondary.main;
};

const getSelectableSx = (status: InteressentenStatus, isSelected: boolean) => (theme: Theme) => {
  const accentColor = getAccentColor(theme, status);

  return {
    borderColor: accentColor,
    borderRadius: 1,
    backgroundColor: isSelected ? alpha(accentColor, 0.14) : theme.palette.background.paper,
    boxShadow: isSelected ? 2 : 0,
    textAlign: 'left' as const,
    transition: theme.transitions.create(['background-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: alpha(accentColor, 0.1),
    },
  };
};

export const InteressentFunnel: FC<InteressentFunnelProps> = ({
  interessenten,
  selectedStatus,
  onStatusChange,
}) => {
  const counts = useMemo(() => {
    const initial = Object.fromEntries(
      interessentenStatusOptions.map(option => [option.value, 0])
    ) as Record<InteressentenStatus, number>;

    return interessenten.reduce((acc, interessent) => {
      acc[getVertriebsStatus(interessent.vertriebsStatus)] += 1;

      return acc;
    }, initial);
  }, [interessenten]);

  const total = interessenten.length;
  // Balkenbreite relativ zur größten Stufe — sonst wäre bei kleinen Beständen kaum ein Balken sichtbar.
  const maxStageCount = Math.max(...funnelStages.map(status => counts[status]), 1);

  const formatShare = (count: number) =>
    total === 0 ? '0 %' : `${Math.round((count / total) * 100)} %`;

  const toggleStatus = (status: InteressentenStatus) =>
    onStatusChange(selectedStatus === status ? '' : status);

  const describe = (status: InteressentenStatus, count: number) =>
    `${getLabel(status)}: ${count} von ${total} Interessenten, ${formatShare(count)}`;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Vertriebsfunnel
      </Typography>

      {total === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Interessenten gefunden.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {funnelStages.map((status, index) => {
              const isSelected = selectedStatus === status;
              const count = counts[status];

              return (
                <ButtonBase
                  key={status}
                  onClick={() => toggleStatus(status)}
                  aria-pressed={isSelected}
                  aria-label={describe(status, count)}
                  sx={theme => ({
                    ...getSelectableSx(status, isSelected)(theme),
                    display: 'block',
                    width: '100%',
                    borderLeft: '3px solid',
                  })}
                >
                  <Box sx={{ px: 1.5, py: 1, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {index + 1}. {getLabel(status)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatShare(count)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                      <Typography variant="h6" sx={{ minWidth: 40 }}>
                        {count}
                      </Typography>
                      <Box
                        sx={theme => ({
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(theme.palette.text.primary, 0.08),
                          overflow: 'hidden',
                        })}
                      >
                        <Box
                          sx={theme => ({
                            width: `${(count / maxStageCount) * 100}%`,
                            height: '100%',
                            borderRadius: 4,
                            backgroundColor: getAccentColor(theme, status),
                            transition: theme.transitions.create('width'),
                          })}
                        />
                      </Box>
                    </Box>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              gap: 1,
              mt: 1.5,
            }}
          >
            {funnelOutcomes.map(status => {
              const isSelected = selectedStatus === status;
              const count = counts[status];

              return (
                <ButtonBase
                  key={status}
                  onClick={() => toggleStatus(status)}
                  aria-pressed={isSelected}
                  aria-label={describe(status, count)}
                  sx={theme => ({
                    ...getSelectableSx(status, isSelected)(theme),
                    minHeight: 72,
                    borderTop: '3px solid',
                  })}
                >
                  <Box sx={{ px: 1.5, py: 1, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {getLabel(status)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatShare(count)}
                      </Typography>
                    </Box>
                    <Typography variant="h6">{count}</Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};
